import { useEffect, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import { useParams } from "react-router-dom";
import { StandaloneCouponsAPI } from "../providers/StandaloneCouponsAPI";
import { Button } from "../shared/components/Button";
import { Empty } from "../shared/components/Empty";
import { Loading } from "../shared/components/Loading";
import { Title } from "../shared/components/Title";
import { If } from "../shared/components/control-statements";
import { Icon } from "../shared/components/core/Icon";
import { BenefitList } from "../shared/containers/beneficios/BenefitList";
import { useIsInViewport } from "../shared/hooks/useIsInViewport";

import styles from "../styles/beneficios/BenefitList.module.scss";
import { Drawer } from "@components";
import { BenefitShowQrCodeStep } from "shared/containers/beneficios/steps/BenefitShowQrCodeStep";
import { BenefitFormDrawer } from "shared/containers/beneficios/BenefitFormDrawer";

export default function ChildrenBenefits() {
  const [parentBenefit, setParentBenefit] = useState(null);
  const [benefits, setBenefits] = useState([]);
  const [hasError, setHasError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [createdBenefit, setCreatedBenefit] = useState(null);
  const [formVisible, setFormVisible] = useState(false);
  const [benefitToEdit, setBenefitToEdit] = useState({});

  const [hasNext, setHasNext] = useState(true);
  const [page, setPage] = useState(0);

  const loadingRef = useRef(null);
  const isVisible = useIsInViewport(loadingRef);

  const urlParams = useParams();
  const parentId = urlParams?.id || null;

  const backTo = parentBenefit?.folder
    ? { url: `/beneficios/pasta/${parentBenefit?.folder}`, name: "Benefícios" }
    : { url: `/beneficios`, name: "Benefícios" };

  const loadBenefits = async (refresh) => {
    setLoading(true);
    const response = await StandaloneCouponsAPI.getStandaloneCoupons(
      "ACTIVATED",
      null,
      page,
      12,
      parentId
    );

    const benefitsPage = response.getData({
      items: [],
      next: { page: -1, limit: 12 },
    });

    let newBenefitsPage;

    if (refresh) {
      newBenefitsPage = [...benefitsPage.items];
    } else {
      newBenefitsPage = [...benefits, ...benefitsPage.items];
    }

    setBenefits(newBenefitsPage);
    setHasNext(benefitsPage.next.page != -1);
    setPage(benefitsPage.next.page != -1 ? page + 1 : page);
    setLoading(false);
  };

  const loadParentBenefit = async () => {
    const parentBenefitResponse =
      await StandaloneCouponsAPI.getStandaloneCouponById(parentId);

    if (parentBenefitResponse.getErrors([]).length > 0) {
      return setHasError(true);
    }

    setParentBenefit(parentBenefitResponse.getData({}));
    setPageLoading(false);
  };

  const createBenefit = async () => {
    setCreateLoading(true);

    const createdBenefitResponse =
      await StandaloneCouponsAPI.createChildrenCoupon(parentId);

    if (createdBenefitResponse.getErrors([]).length > 0) {
      setCreateLoading(false);
      return alert({ text: createdBenefitResponse.getErrors()[0] });
    }

    setCreatedBenefit(createdBenefitResponse.getData({}));
    setCreateLoading(false);
  };

  const resetAndReload = () => {
    setBenefits([]);
    setHasNext(true);
    loadBenefits();
  };

  useEffect(() => {
    if (parentId) {
      loadParentBenefit();
      loadBenefits();
    }
  }, [parentId]);

  useEffect(() => {
    if (hasNext && isVisible) {
      loadBenefits();
    }
  }, [isVisible, page]);

  return pageLoading ? (
    <div className="d-flex flex-column w-100">
      <Loading fullContent />
    </div>
  ) : hasError ? (
    <Empty>
      <Icon name="exclamation-circle" size="4x" light />
      <p>Ops!</p>
      <span>
        Ocorreu um erro ao carregar essa página. Verifique as configurações e
        tente novamente!
      </span>
    </Empty>
  ) : (
    <>
      <Title
        title={`Modelo ${parentBenefit?.code}`}
        subtitle={`Cupons avulsos vinculados`}
        backTo={backTo}
      >
        <Button
          design="primary"
          className={isMobile ? "reverse w-100 mt-15" : "ml-15"}
          loading={createLoading}
          disabled={createLoading}
          onClick={() => createBenefit()}
        >
          Criar benefício
        </Button>
      </Title>

      {benefits.length > 0 && (
        <BenefitList
          benefits={benefits}
          onEditBenefit={(c) => {
            setFormVisible(true);
            setBenefitToEdit(c);
          }}
          onLoadBenefits={() => loadBenefits("refresh")}
        />
      )}

      <If cond={hasNext}>
        <div className={styles.loading}>
          <Loading />
        </div>
        <div ref={loadingRef}></div>
      </If>

      <If cond={benefits.length == 0 && !hasNext && !loading}>
        <Empty>
          <Icon name="ticket-simple" regular size="4x" />
          <p>Não há cupons avulsos cadastrados.</p>
        </Empty>
      </If>

      <Drawer
        title={"Benefício criado com sucesso!"}
        visible={createdBenefit}
        onClose={() => {
          setCreatedBenefit(null);
          resetAndReload();
        }}
        showBackButton={false}
      >
        <BenefitShowQrCodeStep benefit={createdBenefit} />
      </Drawer>

      <BenefitFormDrawer
        visible={formVisible}
        benefitToEdit={benefitToEdit}
        onSave={() => {
          resetAndReload();
        }}
        onClose={() => {
          setFormVisible(false);
          setBenefitToEdit(null);
        }}
      />
    </>
  );
}
